import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom'; // <-- DODANE: importy dla obsługi ścieżek
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import '../styles/AdminProductEdit.css';
import Toast from './Toast';
import ButtonRegular from './ui/ButtonRegular';
import ButtonDelete from './ui/ButtonDelete';

const customColors = [
    '#000000', '#2c3e50', '#4a4a4a', '#7f8c8d', '#95a5a6', '#bdc3c7', '#e0e0e0', '#ffffff',
    '#ffcdd2', '#f8bbd0', '#e1bff7', '#bbdefb', '#b2dfdb', '#c8e6c9', '#fff9c4', '#ffe0b2',
    '#e74c3c', '#ff4d94', '#9b59b6', '#3498db', '#1abc9c', '#2ecc71', '#f1c40f', '#e67e22',
    '#c0392b', '#d42d6a', '#8e44ad', '#2980b9', '#16a085', '#27ae60', '#f39c12', '#d35400',
    '#d81b60', '#8e24aa', '#3f51b5', '#00bcd4', '#4caf50', '#8bc34a', '#ff9800', '#795548',
    '#4a0000', '#4a148c', '#1a237e', '#006064', '#1b5e20', '#33691e', '#5d4037', '#263238',
    '#ff0099'
];

const fontNames = [
	'roboto', 'montserrat', 'playfair-display', 'lora', 'open-sans', 'poppins', 
	'merriweather', 'source-sans-pro', 'oswald', 'inter', 'nunito', 'raleway', 'cormorant-garamond'
];

const Font = Quill.import('formats/font');
Font.whitelist = ['', ...fontNames];
Quill.register(Font, true);

const modules = {
	toolbar: [
		[{ font: ['', ...fontNames] }, { size: ['small', false, 'large', 'huge'] }], 
		['bold', 'italic', 'underline', 'strike'],                    
		[{ color: customColors }],                                              
		[{ list: 'bullet' }],                                         
		[{ align: [] }],                                              
		['clean']                                                     
	],
};

// <-- ZMIANA: Usunięto destrukturyzację propsów (productId, closeForm, onProductUpdated)
const AdminProductEdit = () => {
	const { id: productId } = useParams(); // <-- DODANE: Pobranie ID produktu bezpośrednio z adresu URL (np. /admin/products/edit/:id)
	const navigate = useNavigate(); // <-- DODANE: Inicjalizacja nawigacji do powrotu

	const [product, setProduct] = useState({
		name: '',
		price: '',
		description: '',
		stock: 0,
		categories: [],
		colors: [],
		images: [],
		imageUrl: '',
		internalCode: '',
		height: '',
		width: '',
		depth: '',
		length: '',
		thickness: '',
		weight: '',
		lowestPriceLast30Days: '',
		relatedProducts: [],
	});

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [showPreview, setShowPreview] = useState(false);
	const [previewIndex, setPreviewIndex] = useState(0);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [removedImages, setRemovedImages] = useState([]);
	const [toast, setToast] = useState('');

	const [categories, setCategories] = useState([]);
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [tempCategory, setTempCategory] = useState('');
	const [tempSubCategory, setTempSubCategory] = useState('');
	const [tempSubCategoriesForSelectedCat, setTempSubCategoriesForSelectedCat] = useState([]);

	const [colorsList, setColorsList] = useState([]);
	const [selectedColors, setSelectedColors] = useState([]);
	const [isNewColor, setIsNewColor] = useState(false);
	const [newColorName, setNewColorName] = useState('');
	const [isNewCategory, setIsNewCategory] = useState(false);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [isNewSubCategory, setIsNewSubCategory] = useState(false);
	const [newSubCategoryName, setNewSubCategoryName] = useState('');

	const [relatedProductIds, setRelatedProductIds] = useState([]);
	const [tempRelatedProductId, setTempRelatedProductId] = useState('');
	const [allProducts, setAllProducts] = useState([]);

	useEffect(() => {
		const loadInitialDataAndProduct = async () => {
			try {
				const token = localStorage.getItem('authToken');
				const headers = { Authorization: `Bearer ${token}` };

				const [resCategories, resColors, resProducts] = await Promise.all([
					axios.get(`${process.env.REACT_APP_API_URL}/api/categories`),
					axios.get(`${process.env.REACT_APP_API_URL}/api/colors`),
					axios.get(`${process.env.REACT_APP_API_URL}/api/products`)
				]);

				setCategories(resCategories.data);
				setColorsList(resColors.data);
				setAllProducts(resProducts.data);

				const resProduct = await axios.get(
					`${process.env.REACT_APP_API_URL}/api/products/${productId}`,
					{ headers }
				);
				const data = resProduct.data;
				
				if (data.images && data.images.length > 0) {
					data.images = data.images.map(img => typeof img === 'object' && img !== null ? (img.url || img.path || '') : img);
				} else {
					data.images = data.imageUrl ? [data.imageUrl] : [];
				}

				setProduct(data);
				setSelectedColors(data.colors || []);

				if (data.categories && data.categories.length > 0) {
					const catList = data.categories.map((c) => ({
						categoryId: c.category,
						categoryName: resCategories.data.find((cat) => cat._id === c.category)?.name || '',
						subCategory: c.subCategory || '',
					}));
					setSelectedCategories(catList);
				}

				if (data.relatedProducts && data.relatedProducts.length > 0) {
					const ids = data.relatedProducts.map((item) =>
						typeof item === 'string' ? item : item._id
					);
					setRelatedProductIds(ids);
				}

				setLoading(false);
			} catch (err) {
				console.error('Błąd podczas ładowania danych komponentu', err);
				setError('Błąd podczas ładowania danych');
				setToast('Nie udało się pobrać danych produktu.');
				setLoading(false);
			}
		};

		loadInitialDataAndProduct();
	}, [productId]);

	useEffect(() => {
    	window.scrollTo(0, 0);
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setProduct((prev) => ({ ...prev, [name]: value }));
	};

	const handleDescriptionChange = (content) => {
		setProduct((prev) => {
			if (prev.description === content) return prev;
			return { ...prev, description: content };
		});
	};

	const handleRemoveImage = (img, i) => {
		if (!img) return;
		const isObjectUrl = typeof img === 'string' && (img.startsWith('blob:') || img.startsWith('data:'));
		if (isObjectUrl) {
			setSelectedFiles((prev) => prev.filter((f) => f.preview !== img));
		} else {
			setRemovedImages((prev) => [...prev, img]);
		}
		setProduct((prev) => ({
			...prev,
			images: (prev.images || []).filter((_x, idx) => idx !== i),
		}));
	};

	const moveImage = (fromIndex, toIndex) => {
		setProduct((prev) => {
			const imgs = [...(prev.images || [])];
			if (fromIndex < 0 || toIndex < 0 || fromIndex >= imgs.length || toIndex >= imgs.length)
				return prev;

			const [moved] = imgs.splice(fromIndex, 1);
			imgs.splice(toIndex, 0, moved);

			setSelectedFiles((prevFiles) => {
				const files = [...prevFiles];
				const oldPreview = prev.images ? prev.images[fromIndex] : undefined;
				const fileIdx = files.findIndex((f) => f.preview === oldPreview);
				if (fileIdx === -1) return files;

				const [movedFile] = files.splice(fileIdx, 1);
				const targetPreview = imgs[toIndex];
				const targetFileIdx = files.findIndex((f) => f.preview === targetPreview);

				if (targetFileIdx === -1) {
					files.splice(toIndex, 0, movedFile);
				} else {
					files.splice(targetFileIdx, 0, movedFile);
				}
				return files;
			});

			return { ...prev, images: imgs };
		});
	};

	const createCategory = async () => {
		try {
			const token = localStorage.getItem('authToken');
			const res = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/categories`,
				{ name: newCategoryName },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			const newCat = res.data.category;
			setCategories((prev) => [...prev, newCat]);
			setProduct((p) => ({ ...p, category: newCat._id }));
			return newCat._id;
		} catch (err) {
			console.error('Błąd tworzenia kategorii:', err);
			throw err;
		}
	};

	const createSubCategory = async (catId, subName) => {
		try {
			const token = localStorage.getItem('authToken');
			const res = await axios.patch(
				`${process.env.REACT_APP_API_URL}/api/categories/${catId}/subcategories`,
				{ name: subName },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			const updated = res.data.category.subcategories.map((s) => s.name);
			setProduct((p) => ({ ...p, subCategory: subName }));
			return updated;
		} catch (err) {
			console.error('Błąd tworzenia podkategorii:', err);
			throw err;
		}
	};

	const createColor = async () => {
		const formattedName = newColorName.charAt(0).toUpperCase() + newColorName.slice(1).toLowerCase();
		if (colorsList.some((c) => c.name.toLowerCase() === formattedName.toLowerCase())) {
			setError('Taki kolor już istnieje!');
			return null;
		}
		try {
			const token = localStorage.getItem('authToken');
			const res = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/colors`,
				{ name: formattedName },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			const newColor = res.data.color;
			setColorsList((prev) => [...prev, newColor]);
			setSelectedColors((prev) => [...prev, newColor.name]);
			return newColor.name;
		} catch (err) {
			console.error('Błąd tworzenia koloru:', err);
			setError('Nie udało się dodać koloru');
			return null;
		}
	};

	const removeColor = (colorName) => {
		setSelectedColors((prev) => prev.filter((c) => c !== colorName));
	};

	const addCategoryToProduct = async () => {
		if (!tempCategory) {
			setError('Wybierz kategorię.');
			return;
		}
		let categoryId = tempCategory;
		let categoryName = categories.find((c) => c._id === tempCategory)?.name || '';

		if (tempCategory === 'newCat') {
			if (!newCategoryName.trim()) {
				setError('Podaj nazwę nowej kategorii.');
				return;
			}
			categoryId = await createCategory();
			categoryName = newCategoryName;
			setNewCategoryName('');
		}

		const newEntry = {
			categoryId,
			categoryName,
			subCategory: tempSubCategory || '',
		};

		if (selectedCategories.some((sc) => sc.categoryId === categoryId && sc.subCategory === tempSubCategory)) {
			setError('Ta kategoria + podkategoria już jest dodana.');
			return;
		}

		setSelectedCategories((prev) => [...prev, newEntry]);
		setTempCategory('');
		setTempSubCategory('');
		setTempSubCategoriesForSelectedCat([]);
		setError('');
	};

	const removeCategoryFromProduct = (categoryId, subCategory) => {
		setSelectedCategories((prev) =>
			prev.filter((sc) => !(sc.categoryId === categoryId && sc.subCategory === subCategory))
		);
	};

	const addRelatedProduct = () => {
		if (!tempRelatedProductId.trim()) {
			setError('Wybierz produkt.');
			return;
		}
		if (relatedProductIds.includes(tempRelatedProductId)) {
			setError('Ten produkt już jest dodany.');
			return;
		}
		setRelatedProductIds((prev) => [...prev, tempRelatedProductId]);
		setTempRelatedProductId('');
		setError('');
	};

	const removeRelatedProduct = (id) => {
		setRelatedProductIds((prev) => prev.filter((pid) => pid !== id));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const fd = new FormData();
			selectedFiles.forEach((f) => fd.append('images', f));
			fd.append('name', product.name || '');
			fd.append('price', product.price || '');
			fd.append('description', product.description || '');
			fd.append('stock', product.stock || 0);
			
			const categoriesForRouter = selectedCategories.map((sc) => ({
				category: sc.categoryId,
				subCategory: sc.subCategory || '',
			}));
			fd.append('categories', JSON.stringify(categoriesForRouter));
			
			if (relatedProductIds.length > 0) {
				fd.append('relatedProducts', JSON.stringify(relatedProductIds));
			}
			
			fd.append('internalCode', product.internalCode || '');
			selectedColors.forEach((col) => fd.append('colors', col));
			fd.append('height', product.height || '');
			fd.append('width', product.width || '');
			fd.append('depth', product.depth || '');
			fd.append('length', product.length || '');
			fd.append('thickness', product.thickness || '');
			fd.append('weight', product.weight !== undefined && product.weight !== null ? product.weight : '');
			if (removedImages.length) fd.append('removed', JSON.stringify(removedImages));
			
			const mappedImagesOrder = (product.images || []).map(img => {
				if (typeof img === 'object' && img !== null) {
					return img;
				}
				return { url: img }; 
			});
			fd.append('imagesOrder', JSON.stringify(mappedImagesOrder));
			
			product.colors = selectedColors;

			await axios.put(
				`${process.env.REACT_APP_API_URL}/api/products/${productId}`,
				fd,
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem('authToken')}`,
						'Content-Type': 'multipart/form-data',
					},
				}
			);

			setToast('Produkt został zaktualizowany.');
			
			// <-- ZMIANA: Zamiast wywołania onProductUpdated(), przekierowujemy użytkownika z powrotem na listę produktów po krótkiej chwili (żeby zobaczył Toast)
			setTimeout(() => {
				navigate('/admin/products'); 
			}, 1500);

		} catch (err) {
			console.error('Błąd podczas edytowania produktu', err);
			setError('Nie udało się zaktualizować produktu');
			setToast('Nie udało się zaktualizować produktu.');
		}
	};

	useEffect(() => {
		return () => {
			(product.images || []).forEach((img) => {
				try {
					if (img && typeof img === 'string' && img.startsWith('blob:')) URL.revokeObjectURL(img);
				} catch (e) {}
			});
			selectedFiles.forEach((f) => {
				try {
					if (f && f.preview) URL.revokeObjectURL(f.preview);
				} catch (e) {}
			});
		};
	}, [product.images, selectedFiles]);

	if (loading) return <div>Ładowanie danych produktu...</div>;
	if (error) return <div>{error}</div>;

	return (
		<div className="edit-product-wrap admin-card">
			<h3 className="edit-product-title">Edytuj produkt</h3>

			<div className="edit-product-grid">
				<form className="edit-product-form" onSubmit={handleSubmit}>
					<label>
						Nazwa:
						<input
							className="form-input"
							type="text"
							name="name"
							value={product.name}
							onChange={handleChange}
							required
						/>
					</label>

					<label>
						Kod wewnętrzny:
						<input
							className="form-input"
							type="text"
							name="internalCode"
							value={product.internalCode}
							onChange={handleChange}
							placeholder="np. SKU12345"
						/>
					</label>

					<label>
						Cena:
						<input
							className="form-input"
							type="number"
							step="0.01"
							min="0"
							name="price"
							value={product.price}
							onChange={handleChange}
							onBlur={(e) => {
								const val = e.target.value;
								if (val !== '') {
									handleChange({
										target: {
											name: 'price',
											value: Number(val).toFixed(2),
										},
									});
								}
							}}
							required
						/>
					</label>

					<div style={{ display: 'block', marginBottom: '20px' }}>
						<label>Opis:</label>
						<div className="product-description-editor" style={{ background: '#fff' }}>
							<ReactQuill
								theme="snow"
								modules={modules}
								value={product.description}
								onChange={handleDescriptionChange}
								className="form-quill-editor"
							/>
						</div>
					</div>

					<label>
						Kategorie:
						{!isNewCategory && (
							<select
								value={tempCategory}
								onChange={(e) => {
									const val = e.target.value;
									if (val === 'newCat') {
										setIsNewCategory(true);
										setTempCategory('');
										setTempSubCategory('');
										setTempSubCategoriesForSelectedCat([]);
										return;
									}
									setTempCategory(val);
									const selectedCat = categories.find((c) => c._id === val);
									setTempSubCategoriesForSelectedCat(selectedCat?.subcategories?.map((s) => s.name) || []);
									setTempSubCategory('');
								}}
							>
								<option value="">-- wybierz kategorię --</option>
								{categories.map((cat) => (
									<option key={cat._id} value={cat._id}>
										{cat.name}
									</option>
								))}
								<option value="newCat">➕ Dodaj nową kategorię</option>
							</select>
						)}
						{isNewCategory && (
							<div className="new-cat-box">
								<input
									type="text"
									placeholder="Nowa kategoria"
									value={newCategoryName}
									onChange={(e) => setNewCategoryName(e.target.value)}
								/>
								<button
									type="button"
									onClick={async () => {
										if (!newCategoryName.trim()) return;
										const newCatId = await createCategory();
										setTempCategory(newCatId);
										setIsNewCategory(false);
										setNewCategoryName('');
									}}
								>
									Dodaj
								</button>
								<button
									type="button"
									onClick={() => {
										setIsNewCategory(false);
										setNewCategoryName('');
										setTempCategory('');
									}}
									className="cancel-btn"
								>
									Anuluj
								</button>
							</div>
						)}
					</label>

					<label>
						Podkategoria:
						{!tempCategory && !isNewCategory && (
							<select disabled>
								<option>Najpierw wybierz kategorię</option>
							</select>
						)}
						{isNewCategory && (
							<select disabled>
								<option>Podkategorie dostępne po utworzeniu kategorii</option>
							</select>
						)}
						{!isNewCategory && tempCategory && !isNewSubCategory && (
							<select
								value={tempSubCategory}
								onChange={(e) => {
									if (e.target.value === 'newSub') {
										setIsNewSubCategory(true);
										setTempSubCategory('');
									} else {
										setTempSubCategory(e.target.value);
									}
								}}
							>
								<option value="">-- wybierz podkategorię --</option>
								{tempSubCategoriesForSelectedCat.map((sc, i) => (
									<option key={i} value={sc}>
										{sc}
									</option>
								))}
								<option value="newSub">➕ Dodaj nową podkategorię</option>
							</select>
						)}
						{isNewSubCategory && (
							<div className="new-subcat-box">
								<input
									type="text"
									placeholder="Nowa podkategoria"
									value={newSubCategoryName}
									onChange={(e) => setNewSubCategoryName(e.target.value)}
								/>
								<button
									type="button"
									onClick={async () => {
										if (!newSubCategoryName.trim()) return;
										const updated = await createSubCategory(tempCategory, newSubCategoryName);
										setTempSubCategoriesForSelectedCat(updated);
										setTempSubCategory(newSubCategoryName);
										setIsNewSubCategory(false);
										setNewSubCategoryName('');
									}}
								>
									Dodaj
								</button>
								<button
									type="button"
									onClick={() => {
										setIsNewSubCategory(false);
										setNewSubCategoryName('');
									}}
									className="cancel-btn"
								>
									Anuluj
								</button>
							</div>
						)}
					</label>

					{selectedCategories.length > 0 && (
						<div className="added-categories">
							<h4>Dodane kategorie:</h4>
							<ul>
								{selectedCategories.map((sc, idx) => (
									<li key={idx}>
										<strong>{sc.categoryName}</strong> {sc.subCategory && `/ ${sc.subCategory}`}
										<button
											type="button"
											onClick={() => removeCategoryFromProduct(sc.categoryId, sc.subCategory)}
											className="remove-category-btn"
										>
											✕
										</button>
									</li>
								))}
							</ul>
						</div>
					)}

					{tempCategory && (
						<button type="button" onClick={addCategoryToProduct} className="add-category-btn">
							➕ Dodaj tę kategorię do produktu
						</button>
					)}

					<label>
						Powiązane produkty:
						<div className="related-products-input">
							<select
								value={tempRelatedProductId}
								onChange={(e) => setTempRelatedProductId(e.target.value)}
							>
								<option value="">-- wybierz produkt --</option>
								{allProducts
									.filter((p) => !relatedProductIds.includes(p._id) && p._id !== productId)
									.map((p) => (
										<option key={p._id} value={p._id}>
											{p.name} | {p.internalCode}
										</option>
									))}
							</select>
							<button type="button" onClick={addRelatedProduct} className="add-related-btn">
								➕ Dodaj
							</button>
						</div>
					</label>

					{relatedProductIds.length > 0 && (
						<div className="added-related-products">
							<h4>Dodane powiązane produkty:</h4>
							<ul>
								{relatedProductIds.map((id) => {
									const prod = allProducts.find((p) => p._id === id);
									const firstImg = prod?.images?.[0];
									const relatedImgSrc = typeof firstImg === 'object' && firstImg !== null ? (firstImg.url || firstImg.path || '') : (firstImg || '');

									const fullImgUrl = relatedImgSrc
										? (typeof relatedImgSrc === 'string' && (relatedImgSrc.startsWith('http') || relatedImgSrc.startsWith('data:') || relatedImgSrc.startsWith('blob:'))
											? relatedImgSrc
											: `${process.env.REACT_APP_API_URL}${relatedImgSrc}`)
										: 'https://via.placeholder.com/50';

									return (
										<li key={id} className="related-product-item">
											<img
												src={fullImgUrl}
												alt={prod?.name || 'product'}
												className="related-product-image"
											/>
											<div className="related-product-info">
												<strong>{prod?.name}</strong>
												<small>{prod?.internalCode}</small>
											</div>
											<button
												type="button"
												onClick={() => removeRelatedProduct(id)}
												className="remove-related-btn"
											>
												✕
											</button>
										</li>
									);
								})}
							</ul>
						</div>
					)}

					<label>
						Kolory:
						<div className="selected-colors">
							{selectedColors.map((col) => (
								<span key={col} className="color-tag">
									{col}
									<button
										type="button"
										data-color={col}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											const colorToRemove = e.currentTarget.dataset.color;
											removeColor(colorToRemove);
										}}
									>
										×
									</button>
								</span>
							))}
						</div>

						{!isNewColor ? (
							<select
								onChange={(e) => {
									const val = e.target.value;
									if (val === 'newColor') {
										setIsNewColor(true);
										return;
									}
									if (val && !selectedColors.includes(val)) {
										setSelectedColors((prev) => [...prev, val]);
									}
								}}
							>
								<option value="">-- wybierz kolor --</option>
								{colorsList
									.filter((c) => !selectedColors.includes(c.name))
									.map((c) => (
										<option key={c._id} value={c.name}>
											{c.name}
										</option>
									))}
								<option value="newColor">➕ Dodaj nowy kolor</option>
							</select>
						) : (
							<div className="new-color-box">
								<input
									type="text"
									placeholder="Nowy kolor"
									value={newColorName}
									onChange={(e) => setNewColorName(e.target.value)}
								/>
								<button
									type="button"
									onClick={async () => {
										if (!newColorName.trim()) return;
										const newCol = await createColor();
										if (newCol) setSelectedColors((prev) => [...prev, newCol]);
										setIsNewColor(false);
										setNewColorName('');
									}}
								>
									Dodaj
								</button>
								<button
									type="button"
									onClick={() => {
										setIsNewColor(false);
										setNewColorName('');
									}}
									className="cancel-btn"
								>
									Anuluj
								</button>
							</div>
						)}
					</label>

					<label>
						Stan magazynowy:
						<input
							className="form-input"
							type="number"
							name="stock"
							value={product.stock}
							onChange={handleChange}
							required
						/>
					</label>
					<div className="dim-grid">
						<label>
							Wysokość (cm):
							<input
								className="form-input"
								type="number"
								name="height"
								value={product.height || ''}
								onChange={handleChange}
							/>
						</label>
						<label>
							Szerokość (cm):
							<input
								className="form-input"
								type="number"
								name="width"
								value={product.width || ''}
								onChange={handleChange}
							/>
						</label>
						<label>
							Głębokość (cm):
							<input
								className="form-input"
								type="number"
								name="depth"
								value={product.depth || ''}
								onChange={handleChange}
							/>
						</label>
						<label>
							Długość (cm):
							<input
								className="form-input"
								type="number"
								name="length"
								value={product.length || ''}
								onChange={handleChange}
							/>
						</label>
						<label>
							Grubość (cm):
							<input
								className="form-input"
								type="number"
								name="thickness"
								value={product.thickness || ''}
								onChange={handleChange}
							/>
						</label>
						<label>
							Waga (kg):
							<input
								className="form-input"
								type="number"
								name="weight"
								value={product.weight !== undefined && product.weight !== null ? product.weight : ''}
								onChange={handleChange}
							/>
						</label>
					</div>

					<div className="form-actions">
						<ButtonRegular type="submit">Zaktualizuj produkt</ButtonRegular>
						{/* <-- ZMIANA: Zastąpiono closeForm funkcją navigate do listy produktów */}
						<ButtonDelete type="button" onClick={() => navigate('/admin/products')}>Anuluj</ButtonDelete>
					</div>
				</form>

				<aside className="edit-product-side">
					<div className="image-preview-box">
						<div
							className="drop-area"
							onDragOver={(e) => e.preventDefault()}
							onDrop={(e) => {
								e.preventDefault();
								const files = Array.from(e.dataTransfer.files).filter((f) =>
									f.type.startsWith('image/')
								);
								if (files.length) {
									const withPreviews = files.map((f) => {
										const preview = URL.createObjectURL(f);
										f.preview = preview;
										return f;
									});
									setSelectedFiles((prev) => [...prev, ...withPreviews]);
									const previews = withPreviews.map((f) => f.preview);
									setProduct((p) => ({
										...p,
										images: [...(p.images || []), ...previews],
									}));
								}
							}}
						>
							<div className="drop-instruction">
								Przeciągnij zdjęcia tutaj lub użyj przycisku poniżej
							</div>
							<input
								type="file"
								accept="image/*"
								multiple
								style={{ display: 'none' }}
								id={`editfile-${productId}`}
								onChange={(e) => {
									const files = Array.from(e.target.files || []).filter((f) =>
										f.type.startsWith('image/')
									);
									if (files.length) {
										const withPreviews = files.map((f) => {
											const preview = URL.createObjectURL(f);
											f.preview = preview;
											return f;
										});
										setSelectedFiles((prev) => [...prev, ...withPreviews]);
										const previews = withPreviews.map((f) => f.preview);
										setProduct((p) => ({
											...p,
											images: [...(p.images || []), ...previews],
										}));
									}
								}}
							/>
							<label className="upload-btn" htmlFor={`editfile-${productId}`}>
								Wybierz pliki
							</label>
						</div>

						<div className="thumbnails">
							{(product.images || []).map((img, i) => {
								const imgSrc = typeof img === 'object' && img !== null ? (img.url || img.path || '') : img;

								return (
									<div className="thumb" key={i}>
										<img
											src={(() => {
												if (!imgSrc || typeof imgSrc !== 'string' || imgSrc === '0') return 'https://via.placeholder.com/150';
												if (imgSrc.startsWith('http') || imgSrc.startsWith('data:') || imgSrc.startsWith('blob:')) return imgSrc;
												return `${process.env.REACT_APP_API_URL}${imgSrc}`;
											})()}
											alt={`img-${i}`}
											onClick={() => {
												setPreviewIndex(i);
												setShowPreview(true);
											}}
										/>
										<button
											type="button"
											className="thumb-del"
											onClick={() => handleRemoveImage(img, i)}
										>
											×
										</button>

										<div className="image-order-buttons">
											<button
												type="button"
												aria-label="Przesuń w górę"
												onClick={() => moveImage(i, i - 1)}
												disabled={i === 0}
											>
												↑
											</button>
											<button
												type="button"
												aria-label="Przesuń w dół"
												onClick={() => moveImage(i, i + 1)}
												disabled={i === (product.images ? product.images.length - 1 : 0)}
											>
												↓
											</button>
										</div>
									</div>
								);
							})}
						</div>
					</div>

					<div className="meta">
						<div>
							<strong>ID:</strong> {product._id}
						</div>
					</div>
				</aside>
			</div>

			{showPreview && (
				<div className="image-modal" onClick={() => setShowPreview(false)}>
					<div className="image-modal-inner" onClick={(e) => e.stopPropagation()}>
						<img
							src={(() => {
								const currentImg = (product.images && product.images.length > previewIndex) 
									? product.images[previewIndex] 
									: product.imageUrl;
									
								const checkedSrc = typeof currentImg === 'object' && currentImg !== null ? (currentImg.url || currentImg.path || '') : currentImg;
								
								if (!checkedSrc || typeof checkedSrc !== 'string' || checkedSrc === '0') return 'https://via.placeholder.com/600x400';
								if (checkedSrc.startsWith('http') || checkedSrc.startsWith('data:') || checkedSrc.startsWith('blob:')) return checkedSrc;
								return `${process.env.REACT_APP_API_URL}${checkedSrc}`;
							})()}
							alt={product.name || 'preview'}
						/>
						<ButtonRegular onClick={() => setShowPreview(false)}>
							Zamknij
						</ButtonRegular>
					</div>
				</div>
			)}

			{toast && <Toast message={toast} onClose={() => setToast('')} />}
		</div>
	);
};

export default AdminProductEdit;